/**
 * Notification presentation helpers shared by the header dropdown.
 *
 * Mirrors `mobile/apps/gruv-app/lib/notificationPresentation.ts` so the web
 * bell shows the same glyph per notification type, the same relative age
 * format and the same title/body de-duplication rule as the mobile app.
 */

/** Visual tone of a notification glyph: brand (pink), energy (lime) or muted. */
export type NotificationTone = "primary" | "energy" | "muted";

/** Icon identifiers rendered by `NotificationGlyphIcon`. */
export type NotificationGlyphName =
  | "ticket"
  | "trophy"
  | "people"
  | "person-add"
  | "person"
  | "cash"
  | "paper-plane"
  | "pricetag"
  | "time"
  | "bell";

/** Glyph (icon + tone) resolved for a notification type. */
export interface NotificationGlyph {
  icon: NotificationGlyphName;
  tone: NotificationTone;
}

const TICKET_TYPES = new Set([
  "purchase_confirmed",
  "invitation_received",
  "transfer_received",
  "swap_purchase_confirmed",
]);

const REFUND_TYPES = new Set(["refund_initiated", "refund_confirmed"]);

const AUCTION_TYPES = new Set([
  "auction_expired_no_bids",
  "payment_expired",
  "second_bidder_offered",
  "auction_unblocked",
]);

/**
 * notificationGlyph maps a backend notification type to the icon + tone used by
 * the dropdown row. Unknown types fall back to a muted bell.
 */
export function notificationGlyph(type: string | null | undefined): NotificationGlyph {
  const key = (type ?? "").trim();
  if (TICKET_TYPES.has(key)) return { icon: "ticket", tone: "primary" };
  if (key === "auction_won") return { icon: "trophy", tone: "energy" };
  if (key === "friend_request_accepted") return { icon: "people", tone: "energy" };
  if (key === "friend_request_created") return { icon: "person-add", tone: "primary" };
  if (REFUND_TYPES.has(key)) return { icon: "cash", tone: "muted" };
  if (key === "transfer_sent" || key === "ticket_sold") return { icon: "paper-plane", tone: "muted" };
  if (AUCTION_TYPES.has(key)) return { icon: "pricetag", tone: "muted" };
  if (key === "event_doors_reminder") return { icon: "time", tone: "energy" };
  if (key === "friend_request_rejected") return { icon: "person", tone: "muted" };
  return { icon: "bell", tone: "muted" };
}

/** Labels used by `formatNotificationAge`; callers pass translated strings. */
export interface NotificationAgeLabels {
  /** Under one minute ("ahora"). */
  now: string;
  /** Minutes label, receives the count ("hace 5 min"). */
  minutes: (count: number) => string;
  /** Hours label, receives the count ("hace 3 h"). */
  hours: (count: number) => string;
  /** Days label, receives the count ("hace 2 d"). */
  days: (count: number) => string;
}

/**
 * formatNotificationAge renders a compact relative age like the mobile app:
 * "ahora" under a minute, minutes under an hour, hours under a day, days under
 * a week, and a short numeric day/month after that. Invalid dates return "".
 */
export function formatNotificationAge(
  createdAt: string | null | undefined,
  labels: NotificationAgeLabels,
  locale: string,
  now: Date = new Date(),
): string {
  if (!createdAt) return "";
  const date = new Date(createdAt);
  const ts = date.getTime();
  if (Number.isNaN(ts)) return "";
  const diffMs = Math.max(0, now.getTime() - ts);
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return labels.now;
  if (minutes < 60) return labels.minutes(minutes);
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return labels.hours(hours);
  const days = Math.floor(hours / 24);
  if (days < 7) return labels.days(days);
  return new Intl.DateTimeFormat(locale === "en" ? "en-US" : "es-AR", {
    day: "numeric",
    month: "numeric",
  }).format(date);
}

/**
 * showsNotificationTitle hides the title when it is empty or merely repeats
 * the body (common for legacy rows), matching the mobile row layout.
 */
export function showsNotificationTitle(
  title: string | null | undefined,
  body: string | null | undefined,
): boolean {
  const t = (title ?? "").trim();
  if (!t) return false;
  const b = (body ?? "").trim();
  if (!b) return true;
  return t.toLowerCase() !== b.toLowerCase();
}
